import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Space, Typography, Table } from 'antd';
import moment from 'moment';

import PageHeader from '../../uiKitComponents/pageHeader';
import Form from '../../uiKitComponents/form';
import DatePicker from '../../uiKitComponents/datePicker';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';

import FlowChart from '../../components/flowChart';

import {
  getLifecycleOverviewReport,
  cleanReports,
  setCleanOnUnmountTrue as setReportsCleanOnUnmountTrue,
} from '../../store/actions/reportPagesAction';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const getMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);

const disableMoreThan6MonthsAndFuture = (current) => {
  const today = moment();
  const sixMonthsAgo = today.subtract(6, 'months');
  return (
    current &&
    (current.isBefore(sixMonthsAgo, 'day') || current.isAfter(today, 'day'))
  );
};

const initialState = {
  dates: { from: null, to: null },
};

const columns = [
  {
    dataIndex: 'label',
    title: 'Status',
    defaultSortOrder: 'descend',
    sortDirections: ['descend', 'ascend', 'descend'],
    sorter: (a, b) => a.label.localeCompare(b.label),
  },
  {
    dataIndex: 'count',
    title: 'Count',
    sortDirections: ['descend', 'ascend', 'descend'],
    sorter: (a, b) => a.count - b.count,
  },
];

const TableComponent = ({
  title = '',
  dataSource = [],
  loading = false,
  className,
}) => (
  <div style={{ flex: 1 }} className={className}>
    <Title level={4}>{title}</Title>
    <Table
      size="middle"
      rowKey="status"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      pagination={false}
    />
  </div>
);

const LifecycleOverview = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('lifecycle-overview', {
    ...initialState,
  });
  const { dates } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, lifecycleOverview } = useSelector((store) => ({
    cleanOnUnmount: store.reportPages.cleanOnUnmount,
    lifecycleOverview: store.reportPages.payload.lifecycleOverview,
  }));

  const dispatch = useDispatch();

  const fetchData = useCallback(() => {
    setLoading(true);

    getLifecycleOverviewReport({
      from: dates?.from?.string,
      to: dates?.to?.string,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('lifecycle-overview');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, dates]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  useEffect(() => {
    if (lifecycleOverview?.selected_range) {
      const { from, to } = lifecycleOverview.selected_range;
      form.setFieldsValue({ range: [getMomentDate(from), getMomentDate(to)] });
    }
  }, [lifecycleOverview?.selected_range, form]);

  useMount(() => {
    setReportsCleanOnUnmountTrue()(dispatch);
  });

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('lifecycle-overview');
      cleanReports()(dispatch);
    }
  });

  const onSubmit = ({ range }) => {
    setState((prevState) => ({
      ...prevState,
      dates:
        range && range.length
          ? {
              from: {
                string: moment(range[0].$d).format('DD-MM-YYYY'),
                locale: range[0],
              },
              to: {
                string: moment(range[1].$d).format('DD-MM-YYYY'),
                locale: range[1],
              },
            }
          : initialState.dates,
    }));
  };

  const onReset = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      dates: initialState.dates,
    }));
  };

  if (error)
    return (
      <PageHeader className="page-main-header" title="Lifecycle Overview">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="Lifecycle Overview">
        <Card className="header-card">
          <h3>Select Dates</h3>
          <Form
            form={form}
            onFinish={onSubmit}
            onReset={onReset}
            className="d-flex align-items-center justify-content-between w-100"
          >
            <Form.Item name="range">
              <RangePicker
                format="DD/MM/YYYY"
                disabledDate={disableMoreThan6MonthsAndFuture}
              />
            </Form.Item>
            <Space>
              <Form.Item>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Space>
          </Form>
        </Card>
      </PageHeader>
      <div className="summary-container">
        {Object.entries(lifecycleOverview?.moderation_workload || {}).map(
          ([key, item]) => (
            <button
              type="button"
              key={key}
              className={'summary-el summary-el--read-only'}
            >
              <span className="text-capitalize">
                {key.split('_').join(' ')}:{' '}
              </span>
              <span>{item}</span>
            </button>
          ),
        )}
      </div>
      <div className="page-main-content">
        <div className="d-flex" style={{ gap: 32 }}>
          <FlowChart
            title="Selected Dates"
            data={lifecycleOverview?.flow_range}
            style={{ flex: '1 1 0', minWidth: 0 }}
          />
          <FlowChart
            title="Last 7 Days"
            data={lifecycleOverview?.flow_7d}
            style={{ flex: '1 1 0', minWidth: 0 }}
          />
          <FlowChart
            title="Last 30 Days"
            data={lifecycleOverview?.flow_30d}
            style={{ flex: '1 1 0', minWidth: 0 }}
          />
        </div>
        <div className="d-flex" style={{ gap: 32 }}>
          <TableComponent
            title="Current Status Counts"
            dataSource={lifecycleOverview?.current_counts_by_status}
            loading={loading}
            className="mt-4"
          />
          <TableComponent
            title="Status Counts"
            dataSource={lifecycleOverview?.counts_by_status}
            loading={loading}
            className="mt-4"
          />
          <TableComponent
            title="Funnel"
            dataSource={lifecycleOverview?.funnel}
            loading={loading}
            className="mt-4"
          />
          <TableComponent
            title="Rates"
            dataSource={lifecycleOverview?.rates}
            loading={loading}
            className="mt-4"
          />
        </div>
      </div>
    </div>
  );
};

export default LifecycleOverview;

TableComponent.propTypes = {
  title: PropTypes.string,
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ),
  loading: PropTypes.bool,
  className: PropTypes.string,
};
